import React from "react";
import { motion } from "framer-motion";
import SafeIcon from "../common/SafeIcon";
import * as FiIcons from "react-icons/fi";

const {
  FiZap,
  FiLeaf,
  FiShield,
  FiTrendingUp,
  FiCpu,
  FiAward,
} = FiIcons;

/**
 * SEO-optimised Features section for GreenGasTurbines.com
 * • Keyword-rich headings & descriptions (hydrogen-ready, biogas, RNG, modular CHP, etc.)
 * • Clear metric-based benefit bullets → persuasive & scannable.
 * • CTA aligns with primary site action (Get Quote).
 */
const Features = () => {
  const features = [
    {
      icon: FiLeaf,
      title: "Hydrogen-Ready Combustion",
      description:
        "Burn up to 100% hydrogen, renewable natural gas (RNG) or biogas with ultra-low NOx emissions—future-proofing your power plant against carbon pricing.",
      benefits: [
        "Up to 99% H₂ blend",
        "<9 ppm NOx with DLN burners",
        "Retrofit kits for legacy frames",
      ],
    },
    {
      icon: FiZap,
      title: "Ultra-High Efficiency Core",
      description:
        "Advanced recuperation and closed-loop cooling deliver up to 44% simple-cycle efficiency—slashing fuel spend and boosting ROI.",
      benefits: [
        "44% simple-cycle efficiency",
        ">90% CHP total efficiency",
        "15% lower heat-rate vs. rivals",
      ],
    },
    {
      icon: FiShield,
      title: "24/7 Reliability & Predictive Maintenance",
      description:
        "Rugged alloys and digital twin analytics keep turbines running with 99.5% availability—even in desert or offshore conditions.",
      benefits: [
        "99.5% fleet uptime",
        "25-year design life",
        "AI-driven parts forecasting",
      ],
    },
    {
      icon: FiTrendingUp,
      title: "Modular Scalability up to 50 MW",
      description:
        "Containerized power blocks stack in 5 – 50 MW increments—ideal for data centers, microgrids or utility peakers. Expand without downtime.",
      benefits: [
        "5 – 50 MW flexible blocks",
        "Rapid 48-hour site deployment",
        "N+1 redundancy options",
      ],
    },
    {
      icon: FiCpu,
      title: "AI-Optimized Smart Controls",
      description:
        "Edge-based controllers tune combustion, load-follow renewables and cut start-up time to <5 minutes—maximizing grid revenue.",
      benefits: [
        "Real-time fuel/air tuning",
        "Remote fleet monitoring",
        "<5 min fast-start capability",
      ],
    },
    {
      icon: FiAward,
      title: "Proven Industry Leadership",
      description:
        "Award-winning technology certified by ISO 14001 and recognized by the Global Clean Energy Innovation Awards 2025.",
      benefits: [
        "ISO 14001 & IEC 61800 compliant",
        "500+ units deployed worldwide",
        "Top CleanTech Award 2025",
      ],
    },
  ];

  return (
    <section id="features" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* ─── Section Heading ───────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-secondary-900 mb-6">
            Green Gas Turbine Features&nbsp;&amp;&nbsp;Benefits
          </h2>
          <p className="text-xl text-secondary-600 max-w-3xl mx-auto">
            Discover how our <strong>hydrogen-capable turbines</strong> combine
            record-breaking efficiency, modular design and AI optimization to
            deliver <em>low-carbon, high-ROI power</em> for industrial and
            utility-scale projects worldwide.
          </p>
        </motion.div>

        {/* ─── Feature Grid ───────────────────────────────────── */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, idx) => (
            <motion.article
              key={feature.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: idx * 0.1 }}
              whileHover={{ y: -10, scale: 1.03 }}
              className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
            >
              <div className="flex items-center justify-center w-16 h-16 bg-primary-100 rounded-lg mb-6">
                <SafeIcon icon={feature.icon} className="h-8 w-8 text-primary-600" />
              </div>

              <h3 className="text-xl font-semibold text-secondary-900 mb-4">
                {feature.title}
              </h3>

              <p className="text-secondary-600 mb-6 leading-relaxed">
                {feature.description}
              </p>

              <ul className="space-y-2">
                {feature.benefits.map((benefit) => (
                  <li
                    key={benefit}
                    className="flex items-center text-sm text-secondary-700"
                  >
                    <span className="w-2 h-2 bg-primary-500 rounded-full mr-3"></span>
                    {benefit}
                  </li>
                ))}
              </ul>
            </motion.article>
          ))}
        </div>

        {/* ─── CTA Banner ────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="text-center mt-20"
        >
          <div className="bg-primary-600 rounded-2xl p-8 md:p-12 text-white">
            <h3 className="text-3xl font-bold mb-4">
              Ready to Unlock Low-Carbon Power?
            </h3>
            <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
              Talk with our engineers to model fuel blends, capex &amp; payback for
              your facility in under 48 hours.
            </p>
            <motion.button
              onClick={() =>
                document.querySelector("#quote")?.scrollIntoView({
                  behavior: "smooth",
                })
              }
              className="bg-white text-primary-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus:ring-4 focus:ring-white/30"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Get My Custom Quote
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Features;